<script setup>
import { computed, ref, watch } from 'vue'
import { defaultVTooltipProps } from '@/utils/defaultProps'

const props = defineProps({
  value: {
    type: Boolean,
    default: false,
  },
  buttonTitle: {
    type: String,
    default: null,
  },
  onButtonActivate: {
    type: Function,
    default: () => {},
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  visible: {
    type: Boolean,
    default: true,
  },
  title: {
    type: String,
    default: null,
  },
  maxWidth: {
    type: [String, Number],
    default: '500',
  },
  small: {
    type: Boolean,
    default: false,
  },
  filters: {
    type: Object,
    required: true,
  },
  allowSingleMode: {
    type: Boolean,
    default: false,
  },
  singleMode: {
    type: Boolean,
    default: false,
  },
  showImages: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['input', 'update:singleMode', 'update:showImages', 'update:filters'])

const baseId = ref(`hazard-appearance-settings-${Math.random().toString(36).slice(2, 8)}`)
const dialog = ref(false)
const localHideND = ref(true)

const singleModeModel = computed({
  get: () => props.singleMode,
  set: (value) => emit('update:singleMode', value),
})

const showImagesModel = computed({
  get: () => props.showImages,
  set: (value) => emit('update:showImages', value),
})

const hideNDModel = computed({
  get: () => !localHideND.value,
  set: (value) => {
    localHideND.value = !value
    emit('update:filters', {
      ...props.filters,
      hideND: !value,
    })
  },
})

const activatorButtonId = computed(() => `${baseId.value}-button`)
const singleModeSwitchId = computed(() => `${baseId.value}-single-mode`)
const settingsChanged = computed(() => singleModeModel.value || showImagesModel.value || hideNDModel.value)

watch(
  () => props.value,
  (value) => {
    dialog.value = value
  },
)

watch(dialog, (value) => {
  emit('input', value)
})

watch(
  () => props.filters.hideND,
  (value) => {
    localHideND.value = value ?? true
  },
  { immediate: true },
)
</script>

<template>
  <v-menu
    v-model="dialog"
    offset-y
    :close-on-content-click="false"
    :max-width="maxWidth"
  >
    <template #activator="{ on, attrs }">
      <v-btn
        :id="activatorButtonId"
        v-if="buttonTitle"
        class="border flat-button"
        v-bind="attrs"
        v-on="on"
        :disabled="disabled"
        :style="{ display: visible ? 'flex' : 'none' }"
        :aria-label="title"
        @click="onButtonActivate"
      >
        <template v-if="buttonTitle.startsWith('mdi-')">
          <v-badge
            v-if="settingsChanged"
            dot
            color="primary"
            offset-x="-4"
            offset-y="0"
          >
            <v-icon>
              {{ buttonTitle }}
            </v-icon>
          </v-badge>

          <v-icon v-else>
            {{ buttonTitle }}
          </v-icon>
        </template>

        <template v-else>
          {{ title }}
        </template>
      </v-btn>

      <v-tooltip
        v-if="title"
        top
        v-bind="defaultVTooltipProps"
        :activator="`#${activatorButtonId}`"
      >
        {{ title }}
      </v-tooltip>
    </template>

    <v-card class="settings-menu-card">
      <v-card-title>{{ title }}</v-card-title>

      <v-card-text class="settings-menu-card__content">
        <div class="d-flex flex-column gap-4">
          <div
            v-if="allowSingleMode"
            class="d-flex flex-column gap-1"
          >
            <div class="font-weight-bold">Mode</div>
            <v-switch
              :id="singleModeSwitchId"
              v-model="singleModeModel"
              label="Card mode"
              class="mt-0"
            />

            <v-tooltip
              v-if="allowSingleMode"
              top
              v-bind="defaultVTooltipProps"
              :activator="`#${singleModeSwitchId}`"
            >
              {{ 'Switch to ' + (singleModeModel ? 'Table' : 'Single item') + ' report mode' }}
            </v-tooltip>
          </div>

          <div class="d-flex flex-column gap-1">
            <div class="font-weight-bold">Appearance</div>

            <v-switch
              v-model="hideNDModel"
              :disabled="singleModeModel"
              label="Show Empty"
              hide-details
              class="mt-0"
            />

            <v-switch
              v-model="showImagesModel"
              :disabled="singleModeModel"
              label="Show Structure"
              hide-details
              class="mt-0"
            />

            <!-- <v-checkbox
              v-model="showImagesModel"
              :disabled="singleModeModel"
              label="Show chemical structure"
              hide-details
              class="mt-0"
            /> -->
            <!-- <v-checkbox
              v-model="hideNDModel"
              :disabled="singleModeModel"
              label="Hide chemicals without data"
              hide-details
              class="mt-0"
            /> -->
          </div>
        </div>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn
          @click="dialog = false"
          aria-label="Close"
        >
          Close
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-menu>
</template>

<style scoped>
.single-mode :deep(label) {
  margin-bottom: 0 !important;
}

.single-mode :deep(.v-input__slot) {
  margin-bottom: 0 !important;
}

.single-mode :deep(.v-messages) {
  display: none !important;
}

.settings-menu-card {
  max-height: 80vh;
  display: flex;
  flex-direction: column;
}

.settings-menu-card__content {
  overflow-y: auto;
}
</style>
